import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import axios from '../../../axios';

import { Routes } from '../../../classes/Routes';
import Alert from '../../../components/UI/Alert/Alert';
import Button from '../../../components/UI/Button/Button';
import ConfirmDialog from '../../../components/UI/ConfirmDialog/ConfirmDialog';
import Container from '../../../components/UI/Container/Container';
import { EInputType, IInputField } from '../../../components/UI/Input/Input';
import { useInputs } from '../../../components/UI/Input/useInputs';
import InputGroup from '../../../components/UI/InputGroup/InputGroup';
import ModalContext, { defaultModal, EModalSize } from '../../../components/UI/Modal/ModalContext';
import Spinner from '../../../components/UI/Spinner/Spinner';
import { EInputUpdateAction } from '../../../context/InputContext';
import { EOfferRequestType } from '../../../enums';
import { useAuthUser } from '../../../hooks/useAuthUser';
import { useLocationOptions } from '../../../hooks/useLocationOptions';
import { useOrganizationOptions } from '../../../hooks/useOrganizationOptions';
import { IAppState, IService, IServiceState } from '../../../interfaces';
import { initForm } from '../../../shared/utility';
import * as actions from '../../../store/actions';
import { ETranslation } from '../../../translations/translation-keys';

import { getReportData, IReportResponse } from '../../../services/reportsService';
import { getKnowledgeBase, IServiceKnowledgeBase } from '../../../services/OnboardingService';
import classes from './ServiceDashboard.module.scss';

interface IMatch {
  id: string;
}

interface IProps extends RouteComponentProps<IMatch> {}

export enum EService {
  operator = "operator",
  name = "name",
  url = "url",
  archived = "archived",
  serviceToken = "serviceToken",
  locations = "locations",
  type = "type",
  brandColor = "brandColor",
  heroImage = "heroImage",
  logoBase64Text = "logoBase64Text",
  aiPrompt = "aiPrompt",
  ga4PropertyId = "ga4PropertyId",
  gscPropertyId = "gscPropertyId",
  partnerDomains = "partnerDomains",
}

// Launch checklist steps sourced directly from plan/stepit_launchaamiseen.md
const SETUP_PHASES = [
  {
    id: 'suunnittelu',
    title: '1. Suunnitteluvaihe',
    steps: [
      { id: 'proposal', text: 'HankiX-sopivuusanalyysi & proposal_hanki[PALVELU].md' },
      { id: 'keywords', text: 'Avainsanalista & Google Ads Keyword Planner' },
      { id: 'pricing', text: 'Hinnoittelumalli (pricing_[palvelu].md)' },
      { id: 'competitors', text: 'Kilpailija- ja kohderyhmäanalyysi' },
      { id: 'form_design', text: 'Tarjouspyyntölomakkeen suunnittelu (prd_prompt.md)' }
    ]
  },
  {
    id: 'rakennus',
    title: '2. Rakennusvaihe',
    steps: [
      { id: 'site_gen', text: 'Nettisivun luonti masterpromptilla (site generation)' },
      { id: 'eeat', text: 'EEAT (Tietoa meistä) sivun lisäys' },
      { id: 'tags', text: 'Google Analytics, Tag Manager & Search Console -tagit koodiin' }
    ]
  },
  {
    id: 'parannus',
    title: '3. Parannusvaihe',
    steps: [
      { id: 'dynamic_text', text: 'Kuntasivujen dynaamiset tekstit (municipalityContentTemplates)' },
      { id: 'review', text: 'Koodin review, testaus & refaktorointi' },
      { id: 'seo_check', text: 'SEO-tarkistus (seo_sisaltosuunnitelma_sivulle.md)' },
      { id: 'redirects', text: 'Domainit RedirectFilter.java -luokkaan' }
    ]
  },
  {
    id: 'backend',
    title: '4. Backend-vaihe',
    steps: [
      { id: 'service_token', text: 'serviceToken rekisteröinti backend-puolelle (admin)' },
      { id: 'locations', text: 'Alueiden (Locations) asettaminen palvelulle' },
      { id: 'prompt_instructions', text: 'AI-tarjouspyyntöohjeistus asetettu adminissa' },
      { id: 'workflows', text: 'Automaattiset auditoinnit (/lighthouse, /mobile, /seo, /check-facts)' }
    ]
  },
  {
    id: 'julkaisu',
    title: '5. Julkaisun jälkeen',
    steps: [
      { id: 'domain_zoner', text: 'Domainin vahvistus & Zoner' },
      { id: 'homepage_link', text: 'hankix.fi palvelut-listauksen ja footerin päivitys' },
      { id: 'sitemap', text: 'Sitemap.xml lähetys Search Consoleen' },
      { id: 'recaptcha', text: 'ReCaptcha & Cloudflare Turnstile konfigurointi' },
      { id: 'forms_palaute', text: 'Forms-palautekyselyn aktivointi (lähtee 3 vk päästä)' }
    ]
  },
  {
    id: 'markkinointi',
    title: '6. Markkinoinnin käynnistäminen',
    steps: [
      { id: 'consumer_mkt', text: 'Kuluttajamarkkinointi (Facebook puskaradiot, tiedotteet)' },
      { id: 'first_requests', text: 'Ensimmäiset 5 tarjouspyyntöä vastaanotettu kuluttajilta' },
      { id: 'provider_list', text: 'Palveluntarjoajien haku & kumppanilistauksen teko' },
      { id: 'provider_outreach', text: 'Kontaktointiviestit yrityksille (kirjautumislinkki + ehdot)' }
    ]
  }
];

const Service: React.FC<IProps> = ({ match, history, location }) => {
  const dispatch = useDispatch();
  const { setModal } = useContext(ModalContext);

  const { organizationOptions, organizationsLoading } = useOrganizationOptions({
    withPlaceholder: true,
    withBusinessId: true,
  });

  const { loading, error, service, saveOrUpdateOk } = useSelector<IAppState, IServiceState>(state => state.services);
  const authUser = useAuthUser();
  const { t } = useTranslation();
  const { id } = match.params;

  // Tabs State
  const [activeTab, setActiveTab] = useState<'dashboard' | 'edit' | 'chat'>('dashboard');

  // Reports and KB State
  const [reportData, setReportData] = useState<IReportResponse | null>(null);
  const [reportLoading, setReportLoading] = useState<boolean>(false);
  const [kb, setKb] = useState<IServiceKnowledgeBase | null>(null);

  const calculateLast30DaysLeads = () => {
    if (!reportData || !reportData.timeSeries || reportData.timeSeries.length === 0) return 0;
    const len = reportData.timeSeries.length;
    let sum = 0;
    for (let i = Math.max(0, len - 4); i < len; i++) {
      sum += reportData.timeSeries[i].leads;
    }
    return sum;
  };

  // GSC / GA4 Real Integration states
  const [gscData, setGscData] = useState<{ clicks: number; impressions: number; avgPosition: number; pagesInSearch?: number } | null>(null);
  const [gscLoading, setGscLoading] = useState<boolean>(false);
  const [gscError, setGscError] = useState<string | null>(null);
  const [ga4Data, setGa4Data] = useState<{ activeUsers: number; pageViews: number; conversions: number; conversionRate: number } | null>(null);
  const [ga4Loading, setGa4Loading] = useState<boolean>(false);
  const [ga4Error, setGa4Error] = useState<string | null>(null);

  // Manual Activity state
  const [manualActivities, setManualActivities] = useState<Array<{ id: string; type: string; description: string; date: string }>>([]);
  const [showActivityModal, setShowActivityModal] = useState<boolean>(false);
  const [newActivityType, setNewActivityType] = useState<string>('Sisältöpäivitys');
  const [newActivityDesc, setNewActivityDesc] = useState<string>('');

  // AI Chat state
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'assistant'; text: string }>>([]);
  const [chatInput, setChatInput] = useState<string>('');
  const [chatSending, setChatSending] = useState<boolean>(false);

  // Interactive Checklist states (from stepit_launchaamiseen.md)
  const [checkedSteps, setCheckedSteps] = useState<Record<string, boolean>>({});
  const [showLaunchChecklist, setShowLaunchChecklist] = useState<boolean>(false);
  const [expandedPhases, setExpandedPhases] = useState<Record<string, boolean>>({
    suunnittelu: true,
    rakennus: false,
    parannus: false,
    backend: false,
    julkaisu: false,
    markkinointi: false,
  });

  const [inputs, setInputs] = useState<IInputField>({
    [EService.operator]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.COMMON_OPERATOR_NAME,
      placeholderTranslation: ETranslation.COMMON_OPERATOR_NAME,
      value: "",
      options: [],
      validation: {
        required: authUser?.isPrimary,
      },
    },
    [EService.name]: {
      type: EInputType.text,
      labelTranslation: ETranslation.SERVICE_NAME,
      placeholderTranslation: ETranslation.SERVICE_NAME,
      value: "",
      validation: {
        required: true,
      },
    },
    [EService.url]: {
      type: EInputType.text,
      labelTranslation: ETranslation.SERVICE_URL,
      placeholderTranslation: ETranslation.SERVICE_URL,
      value: "",
    },
    [EService.archived]: {
      type: EInputType.checkbox,
      labelTranslation: ETranslation.COMMON_ARCHIVED,
      placeholderTranslation: ETranslation.COMMON_ARCHIVED,
      value: "",
    },
    [EService.serviceToken]: {
      type: EInputType.static,
      labelTranslation: ETranslation.COMMON_TOKEN,
      value: "",
    },
    [EService.locations]: {
      type: EInputType.reactSelect,
      labelTranslation: ETranslation.LOCATIONS,
      placeholderTranslation: ETranslation.SELECT_LOCATIONS_PLACEHOLDER,
      value: "",
      multiple: true,
    },
    [EService.type]: {
      type: EInputType.radio,
      options: [
        {
          labelTranslation: ETranslation.OFFER_REQUEST_TYPE_SIMPLE,
          value: EOfferRequestType.SIMPLE,
        },
        {
          labelTranslation: ETranslation.OFFER_REQUEST_TYPE_CONSTRUCTION,
          value: EOfferRequestType.CONSTRUCTION,
        },
        {
          labelTranslation: ETranslation.OFFER_REQUEST_TYPE_WELLNESS,
          value: EOfferRequestType.WELLNESS,
        },
      ],
      value: EOfferRequestType.SIMPLE,
    },
    [EService.brandColor]: {
      type: EInputType.color,
      label: "Brändiväri (Pääväri)",
      value: "#E2512C",
    },
    [EService.heroImage]: {
      type: EInputType.text,
      label: "Hero-kuvan URL (esim. https://...)",
      placeholder: "https://",
      value: "",
    },
    [EService.logoBase64Text]: {
      type: EInputType.serviceImage,
      label: "Palvelun logo (esim. brändin logo)",
      value: "",
    },
    [EService.ga4PropertyId]: {
      type: EInputType.text,
      label: "Google Analytics 4 (GA4) Mittauskokonaisuuden ID (Property ID)",
      placeholder: "Esim. 250761496",
      value: "",
    },
    [EService.gscPropertyId]: {
      type: EInputType.text,
      label: "Google Search Console (GSC) Property ID (esim. https://arboristi.hankix.fi/ tai sc-domain:arboristi.hankix.fi)",
      placeholder: "Esim. https://arboristi.hankix.fi/ tai sc-domain:arboristi.hankix.fi",
      value: "",
    },
    [EService.partnerDomains]: {
      type: EInputType.text,
      label: "Kumppanidomainit (pilkulla erotettuna, esim. kumppani.fi, testpartner.com)",
      placeholder: "kumppani.fi, testpartner.com",
      value: "",
    }
  });

  let operatorId;
  if (authUser?.isOperator) {
    operatorId = authUser.organization.id;
  } else {
    operatorId = inputs[EService.operator].value as string;
  }

  const { locationOptions, locationsLoading } = useLocationOptions({
    operatorId,
    filterByOperatorId: true
  });

  const [promptFragmentText, setPromptFragmentText] = useState<string | null>(null);

  // Initialize form / fetch service
  useEffect(() => {
    if (service && service.id && service.operator?.id) {
      axios.post('/json/promptfragments/list', {
        operatorId: service.operator.id,
        serviceId: service.id,
        usage: "SystemInstruction"
      }).then(response => {
        if (response.data && response.data.length > 0) {
          setPromptFragmentText(response.data[0].text);
        }
      }).catch(err => {
        console.error("Error fetching prompt fragment", err);
      });
    }
  }, [service]);

  useEffect(() => {
    if (saveOrUpdateOk) {
      history.push(Routes.SERVICES);
    }
    return () => {
      dispatch(actions.clearService());
    };
  }, [saveOrUpdateOk, history, dispatch]);

  useEffect(() => {
    if (Routes.isNotNew(id)) {
      dispatch(actions.getService(id));
    } else if (authUser && !authUser.isPrimary) {
      history.replace(Routes.SERVICES);
    } else {
      setActiveTab('edit'); // Default to edit tab if creating a new service
    }
  }, [id, dispatch, authUser, history]);

  useEffect(() => {
    if (service) {
      initForm(setInputs, service);
    }
  }, [service]);

  // Fetch Report & Knowledge Base data when dashboard opens
  useEffect(() => {
    if (Routes.isNotNew(id) && service) {
      setReportLoading(true);
      getReportData([id], undefined, 4)
        .then(data => {
          if (data) {
            setReportData(data);
          }
          setReportLoading(false);
        })
        .catch(err => {
          console.error("Failed to load reports for service", err);
          setReportLoading(false);
        });

      if (service.serviceToken) {
        getKnowledgeBase(service.serviceToken)
          .then(data => {
            if (data) {
              setKb(data);
            }
          })
          .catch(err => {
            console.warn("Knowledge base not found or error loading it", err);
          });
      }

      // Load local-storage manual activities
      const saved = localStorage.getItem(`hankix_service_activities_${id}`);
      if (saved) {
        setManualActivities(JSON.parse(saved));
      } else {
        // Seed initial items if empty
        const initial = [
          { id: '1', type: 'Sisältöpäivitys', description: 'Kategorian pääsivun SEO-tekstit ja hakusanat päivitetty.', date: new Date(Date.now() - 48 * 3600 * 1000).toLocaleDateString('fi-FI') },
          { id: '2', type: 'B2B Kontaktointi', description: 'Tavoiteltu 5 uutta kumppania alueelta Uusimaa.', date: new Date(Date.now() - 120 * 3600 * 1000).toLocaleDateString('fi-FI') }
        ];
        setManualActivities(initial);
        localStorage.setItem(`hankix_service_activities_${id}`, JSON.stringify(initial));
      }
    }
  }, [id, service]);

  // Load / initialize interactive launch checklist
  useEffect(() => {
    if (Routes.isNotNew(id) && service) {
      const saved = localStorage.getItem(`hankix_setup_steps_${id}`);
      if (saved) {
        setCheckedSteps(JSON.parse(saved));
      } else {
        // Auto-detect initial checks based on database attributes!
        const initial: Record<string, boolean> = {};
        
        // 1. Suunnittelu
        if (service.name) {
          initial['proposal'] = true;
        }
        if (kb) {
          initial['competitors'] = true;
        }
        
        // 2. Rakennus
        if (service.brandColor && service.brandColor !== '#E2512C') {
          initial['site_gen'] = true;
        }
        
        // 4. Backend
        if (service.serviceToken) {
          initial['service_token'] = true;
        }
        if (service.locations && service.locations.length > 0) {
          initial['locations'] = true;
        }
        if (promptFragmentText) {
          initial['prompt_instructions'] = true;
        }
        
        setCheckedSteps(initial);
        localStorage.setItem(`hankix_setup_steps_${id}`, JSON.stringify(initial));
      }
    }
  }, [id, service, promptFragmentText, kb]);

  // Fetch real Google Search Console and Google Analytics 4 metrics automatically
  useEffect(() => {
    if (id && Routes.isNotNew(id) && service) {
      setGscLoading(true);
      setGscError(null);
      axios.get(`/json/services/googlesearchconsole?id=${id}`)
        .then(res => {
          setGscData(res.data);
          setGscError(null);
          setGscLoading(false);
        })
        .catch(err => {
          console.warn("Error fetching GSC data", err);
          const errMsg = err.response?.data || "Ei kytketty Google Search Consoleen tai varmennus puuttuu.";
          setGscError(errMsg);
          setGscData(null);
          setGscLoading(false);
        });

      setGa4Loading(true);
      setGa4Error(null);
      axios.get(`/json/services/googleanalytics4?id=${id}`)
        .then(res => {
          setGa4Data(res.data);
          setGa4Error(null);
          setGa4Loading(false);
        })
        .catch(err => {
          console.warn("Error fetching GA4 data", err);
          const errMsg = err.response?.data || "Ei kytketty Google Analyticsiin tai GA4-määritykset puuttuvat.";
          setGa4Error(errMsg);
          setGa4Data(null);
          setGa4Loading(false);
        });
    }
  }, [id, service]);

  const { createInput, submit } = useInputs({
    inputs,
    setInputs,
    options: {
      updateAction: EInputUpdateAction.SERVICE
    }
  });

  const submitHandler = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();

    const service = await submit() as IService;
    if (!service) return;

    if (Routes.isNew(id)) {
      dispatch(actions.saveService(service));
    } else {
      service.id = id;
      dispatch(actions.updateService(service));
    }
  };

  const addAllLocationsHandler = () => {
    setInputs((inputs) => {
      const newInputs = { ...inputs };
      newInputs[EService.locations].value = locationOptions.map(opt => opt.value);
      return newInputs;
    });
  };

  const setConfirmDeleteDialog = () =>
    setModal({
      isOpen: true,
      title: t(ETranslation.COMMON_REMOVE),
      size: EModalSize.SMALL,
      content: (
        <ConfirmDialog
          body={<p>{t(ETranslation.CONFIRM_DELETE_SERVICE)}</p>}
          onConfirm={() => {
            dispatch(actions.deleteService(id));
            history.goBack();
            setModal(defaultModal);
          }}
          onCancel={() => setModal(defaultModal)}
        />
      ),
    });

  // Calculate dynamic launch setup progress (0-100%) from interactive checklist
  const calculateSetupProgress = () => {
    const totalSteps = SETUP_PHASES.reduce((acc, phase) => acc + phase.steps.length, 0);
    const checkedCount = Object.values(checkedSteps).filter(Boolean).length;
    if (totalSteps === 0) return 0;
    return Math.round((checkedCount / totalSteps) * 100);
  };

  // Calculate dynamic health/activity Pulse score (0-100%)
  const calculatePulseScore = () => {
    let pulse = 40; // category starts active
    if (reportData) {
      if (reportData.summary.activeProviders > 0) pulse += 20;
      if (reportData.summary.totalLeads > 0) pulse += 20;
      if (reportData.summary.leadChangePercent > 0) pulse += 10;
    }
    if (manualActivities.length > 2) pulse += 10;
    return Math.min(pulse, 100);
  };

  const handleAddActivitySubmit = () => {
    if (!newActivityDesc.trim()) return;
    const newAct = {
      id: Math.random().toString(),
      type: newActivityType,
      description: newActivityDesc.trim(),
      date: new Date().toLocaleDateString('fi-FI')
    };
    const updated = [newAct, ...manualActivities];
    setManualActivities(updated);
    localStorage.setItem(`hankix_service_activities_${id}`, JSON.stringify(updated));
    setNewActivityDesc('');
    setShowActivityModal(false);
  };

  // Chat sending handler
  const handleSendChatMessage = async (presetPrompt?: string) => {
    const promptToSend = presetPrompt || chatInput;
    if (!promptToSend.trim() || chatSending) return;

    const newMsgs = [...chatMessages, { role: 'user' as const, text: promptToSend }];
    setChatMessages(newMsgs);
    setChatInput('');
    setChatSending(true);

    try {
      const response = await axios.post('/json/services/chat', {
        serviceId: id,
        prompt: promptToSend
      });

      if (response.data && response.data.response) {
        setChatMessages([...newMsgs, { role: 'assistant', text: response.data.response }]);
      } else {
        setChatMessages([...newMsgs, { role: 'assistant', text: "Pahoittelut, en kyennyt vastaamaan kysymykseesi tällä kertaa." }]);
      }
    } catch (err) {
      console.error("Chat error", err);
      setChatMessages([...newMsgs, { role: 'assistant', text: "Yhteysvirhe tekoälypalveluun. Yritäthän hetken kuluttua uudelleen." }]);
    } finally {
      setChatSending(false);
    }
  };

  const handleToggleStep = (stepId: string) => {
    const updated = { ...checkedSteps, [stepId]: !checkedSteps[stepId] };
    setCheckedSteps(updated);
    localStorage.setItem(`hankix_setup_steps_${id}`, JSON.stringify(updated));
  };

  const handleTogglePhase = (phaseId: string) => {
    setExpandedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  const getPhaseCheckedCount = (phaseId: string) => {
    const phase = SETUP_PHASES.find(p => p.id === phaseId);
    if (!phase) return 0;
    return phase.steps.filter(step => checkedSteps[step.id]).length;
  };

  // Setup initial chat message
  useEffect(() => {
    if (service && chatMessages.length === 0) {
      setChatMessages([
        {
          role: 'assistant',
          text: `Hei! Olen HankiX AI-Avustajasi. Olen perehtynyt palvelukategoriaan **${service.name}** ja sen tietopohjaan.\n\nMiten voin auttaa sinua kehittämään ja kasvattamaan tätä kategoriaa tänään? Voit kysyä esimerkiksi vinkkejä palveluntarjoajien kontaktointiin, tehokkaimpia SEO-avainsanoja tai ideoita kampanjointiin.`
        }
      ]);
    }
  }, [service, chatMessages]);

  return (
    <Container>
      {error && <Alert children={error} />}

      {/* Header section with brand color indicators */}
      <div className={classes.DashboardHeader}>
        <div className={classes.HeaderLeft}>
          {inputs[EService.logoBase64Text]?.value ? (
            <img src={inputs[EService.logoBase64Text].value as string} alt="Logo" className={classes.ServiceLogo} />
          ) : (
            <div className={classes.ServiceLogo} style={{ background: inputs[EService.brandColor]?.value as string || '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1.5rem', fontWeight: 'bold' }}>
              {(inputs[EService.name]?.value as string || 'H')[0]}
            </div>
          )}
          <div>
            <h1 style={{ borderLeft: `5px solid ${inputs[EService.brandColor]?.value || '#4f46e5'}`, paddingLeft: '10px' }}>
              {Routes.isNew(id) ? t(ETranslation.TITLE_SERVICE_NEW) : (inputs[EService.name]?.value as string)}
            </h1>
            <p className={classes.Subtitle}>
              {!Routes.isNew(id) && `${t(ETranslation.COMMON_TOKEN)}: ${service?.serviceToken || '-'}`}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs Row (Only shown if service exists) */}
      {Routes.isNotNew(id) && (
        <div className={classes.TabRow}>
          <button className={`${classes.TabButton} ${activeTab === 'dashboard' ? classes.ActiveTab : ''}`} onClick={() => setActiveTab('dashboard')}>
            Työpöytä (Dashboard)
          </button>
          <button className={`${classes.TabButton} ${activeTab === 'edit' ? classes.ActiveTab : ''}`} onClick={() => setActiveTab('edit')}>
            Brändäys & Asetukset
          </button>
          <button className={`${classes.TabButton} ${activeTab === 'chat' ? classes.ActiveTab : ''}`} onClick={() => setActiveTab('chat')}>
            AI-Avustaja
          </button>
        </div>
      )}

      {activeTab === 'dashboard' && Routes.isNotNew(id) && (
        <div>
          {/* Progress / Pulse Section */}
          <div className={classes.ProgressGrid}>
            <div className={classes.ProgressCard}>
              <h3>
                Palvelun pystytys (Setup Progress)
                <span style={{ fontSize: '0.85rem', color: '#4f46e5', fontWeight: 800 }}>{calculateSetupProgress()}%</span>
              </h3>
              <div className={classes.ProgressBarOuter}>
                <div className={classes.ProgressBarInner} style={{ width: `${calculateSetupProgress()}%`, background: inputs[EService.brandColor]?.value as string || '#4f46e5' }} />
              </div>
              <div className={classes.ProgressLabel}>
                <span>Vaihe {calculateSetupProgress() === 100 ? '6/6: Valmis' : calculateSetupProgress() >= 80 ? '5/6: Julkaisu' : calculateSetupProgress() >= 45 ? '3/6: Parannukset & Backend' : '1/6: Suunnittelu'}</span>
                <span>Tavoite: 100%</span>
              </div>
              <div>
                <button className={classes.ChecklistToggleBtn} onClick={() => setShowLaunchChecklist(!showLaunchChecklist)}>
                  {showLaunchChecklist ? '▲ Piilota lanseerausvaiheet' : '▼ Näytä lanseerausvaiheet (Playbook)'}
                </button>
              </div>
            </div>

            <div className={classes.ProgressCard}>
              <h3>
                Jatkuva aktiivisuus (Pulse / Health)
                <span className={`${classes.PulseBadge} ${calculatePulseScore() >= 80 ? classes.Green : calculatePulseScore() >= 50 ? classes.Yellow : classes.Red}`}>
                  {calculatePulseScore() >= 80 ? 'ERINOMAINEN' : calculatePulseScore() >= 50 ? 'TYYDYTTÄVÄ' : 'HEIKKO'}
                </span>
              </h3>
              <div className={classes.ProgressBarOuter}>
                <div className={classes.ProgressBarInner} style={{ width: `${calculatePulseScore()}%`, background: calculatePulseScore() >= 80 ? '#10b981' : calculatePulseScore() >= 50 ? '#f59e0b' : '#ef4444' }} />
              </div>
              <div className={classes.ProgressLabel}>
                <span>Terveysindeksi: {calculatePulseScore()}%</span>
                <span>Viimeisin aktiviteetti tänään</span>
              </div>
            </div>
          </div>

          {/* Collapsible Playbook Launch Checklist Section */}
          {showLaunchChecklist && (
            <div className={classes.ChecklistSection}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '0 0 0.5rem 0', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>
                  Lanseerauksen tarkistuslista (Playbook)
                </h3>
                <span style={{ fontSize: '0.65rem', background: '#e0f2fe', color: '#0369a1', padding: '0.15rem 0.4rem', borderRadius: '4px', fontWeight: 700 }}>SELAINKOHTAINEN TALLENNUS (LOCALSTORAGE)</span>
              </div>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#64748b' }}>
                Seuraa uuden kategoriavertikaalin pystytysvaiheita. Merkinnät tallentuvat selaimeesi automaattisesti ja päivittävät pystytysmittaria! (Jatkovaihe: tietokantasynkronointi)
              </p>
              {SETUP_PHASES.map(phase => (
                <div key={phase.id} style={{ display: 'flex', flexDirection: 'column' }}>
                  <div className={classes.PhaseHeader} onClick={() => handleTogglePhase(phase.id)}>
                    <span>
                      {expandedPhases[phase.id] ? '▼' : '►'} {phase.title}
                    </span>
                    <span className={classes.PhaseProgress}>
                      {getPhaseCheckedCount(phase.id)} / {phase.steps.length} valmis
                    </span>
                  </div>
                  {expandedPhases[phase.id] && (
                    <div className={classes.PhaseStepsList}>
                      {phase.steps.map(step => (
                        <label key={step.id} className={classes.StepItem} onClick={() => handleToggleStep(step.id)}>
                          <input 
                            type="checkbox" 
                            checked={!!checkedSteps[step.id]} 
                            onChange={() => {}} // handled by onClick on wrapper
                          />
                          <span className={checkedSteps[step.id] ? classes.CheckedText : ''}>
                            {step.text}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* KPI Cards (Oma tietokanta) */}
          <div className={classes.KpiGrid} style={{ marginTop: showLaunchChecklist ? '1.5rem' : '0' }}>
            <div className={classes.KpiCard}>
              <div className={classes.CardHeader}>
                <span>Tarjouspyynnöt (Oma tietokanta)</span>
              </div>
              <div className={classes.CardBody}>
                <div style={{ display: 'flex', gap: '2rem', marginBottom: '0.75rem' }}>
                  <div>
                    <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', fontWeight: 600 }}>Tällä viikolla</span>
                    <div className={classes.Number} style={{ fontSize: '1.8rem', lineHeight: 1.2 }}>
                      {reportLoading ? <Spinner /> : reportData?.summary.currentWeekLeads ?? 0}
                    </div>
                  </div>
                  <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: '1.5rem' }}>
                    <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', fontWeight: 600 }}>Viimeiset 30 päivää</span>
                    <div className={classes.Number} style={{ fontSize: '1.8rem', lineHeight: 1.2, color: '#4f46e5' }}>
                      {reportLoading ? <Spinner /> : calculateLast30DaysLeads()}
                    </div>
                  </div>
                </div>
                {reportData && (
                  <div className={`${classes.Trend} ${reportData.summary.leadChangePercent >= 0 ? classes.Positive : classes.Negative}`}>
                    {reportData.summary.leadChangePercent >= 0 ? '▲' : '▼'} {Math.abs(Math.round(reportData.summary.leadChangePercent))}%
                    <span className={classes.TrendLabel}>edell. viikko</span>
                  </div>
                )}
              </div>
            </div>

            <div className={classes.KpiCard}>
              <div className={classes.CardHeader}>
                <span>Palveluntarjoajat (Providers)</span>
              </div>
              <div className={classes.CardBody}>
                <div className={classes.Number}>
                  {reportLoading ? <Spinner /> : reportData?.summary.activeProviders ?? 0}
                </div>
                <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
                  Aktiivista palveluluokassa
                </span>
              </div>
            </div>

            <div className={classes.KpiCard}>
              <div className={classes.CardHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Match Rate</span>
                <span style={{ fontSize: '0.6rem', background: '#dcfce7', color: '#15803d', padding: '0.15rem 0.4rem', borderRadius: '4px', fontWeight: 700, letterSpacing: '0.025em' }}>REAALIAIKAINEN</span>
              </div>
              <div className={classes.CardBody}>
                <div className={classes.Number}>
                  {reportLoading ? <Spinner /> : `${Math.round(reportData?.summary.matchRatePercent ?? 0)}%`}
                </div>
                <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
                  Käsiteltyt tarjouspyynnöt ({reportData?.summary.matchedLeads ?? 0} / {reportData?.summary.totalLeads ?? 0})
                </span>
                {reportData?.summary.needsAttentionLeads ? (
                  <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.5rem', fontWeight: 600 }}>
                    🔴 {reportData.summary.needsAttentionLeads} tarjouspyyntöä vaatii huomiota
                  </div>
                ) : (
                  <div style={{ fontSize: '0.75rem', color: '#16a34a', marginTop: '0.5rem', fontWeight: 600 }}>
                    🟢 Kaikki tarjouspyynnöt huomioitu
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Vaste / SEO & GA4 Real metrics and setup guides */}
          <div className={classes.VasteGrid}>
            <div className={classes.VasteCard}>
              <h3>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Orgaaninen löydettävyys (Google Search Console)
                  <a 
                    href={inputs[EService.url]?.value 
                      ? `https://search.google.com/search-console?resource_id=https://${(inputs[EService.url].value as string).replace(/^https?:\/\//, '').replace(/\/$/, '')}/` 
                      : 'https://search.google.com/search-console'} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ color: '#94a3b8', marginLeft: '6px', transition: 'color 0.2s', display: 'inline-flex', alignItems: 'center' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                    title="Avaa Google Search Console"
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      style={{ width: '13px', height: '13px' }}
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                </span>
                <span className={classes.VasteBadge} style={{ background: gscLoading ? '#f1f5f9' : (gscError ? '#fef2f2' : '#ecfdf5'), color: gscLoading ? '#475569' : (gscError ? '#dc2626' : '#059669'), border: '1px solid ' + (gscLoading ? '#e2e8f0' : (gscError ? '#fecaca' : '#a7f3d0')) }}>
                  {gscLoading ? 'Päivitetään...' : (gscError ? 'Integroimatta' : '● Yhdistetty')}
                </span>
              </h3>
              <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginTop: '-0.25rem', marginBottom: '0.75rem', fontWeight: 500 }}>
                Edelliset 30 vuorokautta (3 päivän tiedonsynkronointiviiveellä)
              </span>

              {gscLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem', gap: '0.5rem' }}>
                  <Spinner />
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Ladataan Google Search Console -tietoja...</span>
                </div>
              ) : gscError ? (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1rem', marginTop: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#1e293b', fontSize: '0.9rem', fontWeight: 800 }}>Miten kytkeä Google Search Console (GSC) käyttöön?</h4>
                  <p style={{ margin: '0 0 0.75rem 0', color: '#64748b', fontSize: '0.8rem', lineHeight: 1.4 }}>
                    Sivustoa ei ole vielä vahvistettu tai palvelutunnuksella ei ole oikeutta lukea sivuston tietoja. Ota GSC käyttöön seuraavasti:
                  </p>
                  <ol style={{ margin: 0, paddingLeft: '1.2rem', color: '#475569', fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: '0.4rem', lineHeight: 1.4 }}>
                    <li>Varmista, että sivuston verkkotunnus (esim. <strong>{inputs[EService.url]?.value as string || 'domain'}</strong>) on lisätty Google Search Consoleen.</li>
                    <li>Lisää HankiX:n palvelutunnus käyttäjäksi GSC-omaisuuteen:
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.35rem 0', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '0.25rem 0.5rem', fontFamily: 'monospace', fontSize: '0.75rem', color: '#0f172a', wordBreak: 'break-all' }}>
                        <span>search-console-reader@hankix.iam.gserviceaccount.com</span>
                        <button 
                          onClick={() => {
                            navigator.clipboard.writeText('search-console-reader@hankix.iam.gserviceaccount.com');
                            alert('Sähköposti kopioitu leikepöydälle!');
                          }} 
                          style={{ marginLeft: 'auto', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '0.1rem 0.35rem', fontSize: '0.7rem', cursor: 'pointer', fontWeight: 700 }}
                        >
                          Kopioi
                        </button>
                      </div>
                    </li>
                    <li>Anna palvelutunnukselle vähintään <strong>Lukija (Reader)</strong> -oikeudet GSC:ssä.</li>
                    <li>Oikeuksien myöntämisen jälkeen tiedot synkronoituvat ja päivittyvät tähän näkymään automaattisesti.</li>
                  </ol>
                  <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: '#fffbef', border: '1px solid #fef3c7', borderRadius: '6px', color: '#b45309', fontSize: '0.75rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <span>Huomautus: {gscError}</span>
                  </div>
                </div>
              ) : (
                <div className={classes.VasteMetricRow}>
                  <div className={classes.VasteMetric}>
                    <span className={classes.Label}>Impressiot</span>
                    <span className={classes.Value}>
                      {gscData ? gscData.impressions.toLocaleString('fi-FI') : '0'}
                    </span>
                  </div>
                  <div className={classes.VasteMetric}>
                    <span className={classes.Label}>Klikkaukset</span>
                    <span className={classes.Value}>
                      {gscData ? gscData.clicks.toLocaleString('fi-FI') : '0'}
                    </span>
                  </div>
                  <div className={classes.VasteMetric}>
                    <span className={classes.Label}>Klikki %</span>
                    <span className={classes.Value}>
                      {gscData ? (gscData.impressions > 0 ? ((gscData.clicks / gscData.impressions) * 100).toFixed(1) + ' %' : '0.0 %') : '0.0 %'}
                    </span>
                  </div>
                  <div className={classes.VasteMetric}>
                    <span className={classes.Label}>Keskim. Sija</span>
                    <span className={classes.Value}>
                      {gscData ? gscData.avgPosition : '0.0'}
                    </span>
                  </div>
                  <div className={classes.VasteMetric}>
                    <span className={classes.Label}>Uniikit sivut</span>
                    <span className={classes.Value}>
                      {gscData ? (gscData.pagesInSearch !== undefined ? gscData.pagesInSearch : 0) : '0'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className={classes.VasteCard}>
              <h3>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Liikenne ja konversiot (Google Analytics 4)
                  <a 
                    href="https://analytics.google.com/analytics/web/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ color: '#94a3b8', marginLeft: '6px', transition: 'color 0.2s', display: 'inline-flex', alignItems: 'center' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                    title="Avaa Google Analytics"
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      style={{ width: '13px', height: '13px' }}
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                </span>
                <span className={classes.VasteBadge} style={{ background: ga4Loading ? '#f1f5f9' : (ga4Error ? '#fef2f2' : '#ecfdf5'), color: ga4Loading ? '#475569' : (ga4Error ? '#dc2626' : '#059669'), border: '1px solid ' + (ga4Loading ? '#e2e8f0' : (ga4Error ? '#fecaca' : '#a7f3d0')) }}>
                  {ga4Loading ? 'Päivitetään...' : (ga4Error ? 'Integroimatta' : '● Live')}
                </span>
              </h3>
              <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginTop: '-0.25rem', marginBottom: '0.75rem', fontWeight: 500 }}>
                Edelliset 30 vuorokautta (Konversio % = Tarjouspyynnöt / Käyttäjät)
              </span>

              {ga4Loading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem', gap: '0.5rem' }}>
                  <Spinner />
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Ladataan Google Analytics -tietoja...</span>
                </div>
              ) : ga4Error ? (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1rem', marginTop: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#1e293b', fontSize: '0.9rem', fontWeight: 800 }}>Miten kytkeä Google Analytics 4 (GA4) käyttöön?</h4>
                  <p style={{ margin: '0 0 0.75rem 0', color: '#64748b', fontSize: '0.8rem', lineHeight: 1.4 }}>
                    Sivustoa ei ole kytketty Google Analyticsiin tai tälle verkkotunnukselle ei ole vielä asetettu GA4 Property ID -määritystä. Seuraa näitä ohjeita:
                  </p>
                  <ol style={{ margin: 0, paddingLeft: '1.2rem', color: '#475569', fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: '0.4rem', lineHeight: 1.4 }}>
                    <li>Varmista, että sivustolle on luotu Google Analytics 4 (GA4) -omaisuus.</li>
                    <li>Lisää HankiX:n palvelutunnus käyttäjäksi GA4-omaisuuteesi:
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.35rem 0', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '0.25rem 0.5rem', fontFamily: 'monospace', fontSize: '0.75rem', color: '#0f172a', wordBreak: 'break-all' }}>
                        <span>search-console-reader@hankix.iam.gserviceaccount.com</span>
                        <button 
                          onClick={() => {
                            navigator.clipboard.writeText('search-console-reader@hankix.iam.gserviceaccount.com');
                            alert('Sähköposti kopioitu leikepöydälle!');
                          }} 
                          style={{ marginLeft: 'auto', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '0.1rem 0.35rem', fontSize: '0.7rem', cursor: 'pointer', fontWeight: 700 }}
                        >
                          Kopioi
                        </button>
                      </div>
                    </li>
                    <li>Anna palvelutunnukselle vähintään <strong>Lukija (Viewer)</strong> -oikeudet.</li>
                    <li>Toimita sivuston <strong>GA4 Property ID</strong> (esim. <em>434551139</em>) ylläpidolle kytkentää varten.</li>
                  </ol>
                  <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: '#fffbef', border: '1px solid #fef3c7', borderRadius: '6px', color: '#b45309', fontSize: '0.75rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <span>Huomautus: {ga4Error}</span>
                  </div>
                </div>
              ) : (
                <div className={classes.VasteMetricRow}>
                  <div className={classes.VasteMetric}>
                    <span className={classes.Label}>Käyttäjät</span>
                    <span className={classes.Value}>
                      {ga4Data ? ga4Data.activeUsers.toLocaleString('fi-FI') : '0'}
                    </span>
                  </div>
                  <div className={classes.VasteMetric}>
                    <span className={classes.Label}>Katselut</span>
                    <span className={classes.Value}>
                      {ga4Data ? ga4Data.pageViews.toLocaleString('fi-FI') : '0'}
                    </span>
                  </div>
                  <div className={classes.VasteMetric}>
                    <span className={classes.Label}>Tarjouspyynnöt</span>
                    <span className={classes.Value}>
                      {ga4Data ? ga4Data.conversions.toLocaleString('fi-FI') : '0'}
                    </span>
                  </div>
                  <div className={classes.VasteMetric}>
                    <span className={classes.Label}>Konversio %</span>
                    <span className={classes.Value}>
                      {ga4Data ? ga4Data.conversionRate.toFixed(1) + ' %' : '0.0 %'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main Layout containing Activity feed (Audit trail) & Action Items */}
          <div className={classes.MainLayout}>
            <div className={classes.FeedCard}>
              <div className={classes.FeedHeader}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <h3 style={{ margin: 0 }}>Viimeisimmät toimenpiteet (Activity Feed)</h3>
                  <span style={{ fontSize: '0.65rem', color: '#0369a1', fontWeight: 700, letterSpacing: '0.025em' }}>
                    SELAINKOHTAINEN TALLENNUS (LOCALSTORAGE)
                  </span>
                </div>
                <button className={classes.AddActionButton} onClick={() => setShowActivityModal(true)}>
                  + Kirjaa toimenpide
                </button>
              </div>

              <div className={classes.ActivityList}>
                {manualActivities.map(act => (
                  <div key={act.id} className={classes.ActivityItem}>
                    <div className={classes.ActivityIcon} style={{ background: act.type === 'Sisältöpäivitys' ? '#e0f2fe' : act.type === 'B2B Kontaktointi' ? '#fef3c7' : act.type === 'Some-kampanja' ? '#f3e8ff' : '#ecfdf5', color: act.type === 'Sisältöpäivitys' ? '#0369a1' : act.type === 'B2B Kontaktointi' ? '#b45309' : act.type === 'Some-kampanja' ? '#6b21a8' : '#047857' }}>
                      {act.type === 'Sisältöpäivitys' ? '📝' : act.type === 'B2B Kontaktointi' ? '📞' : act.type === 'Some-kampanja' ? '📣' : '🚀'}
                    </div>
                    <div className={classes.ActivityContent}>
                      <div className={classes.Title}>{act.type}</div>
                      <div className={classes.Desc}>{act.description}</div>
                      <div className={classes.Time}>{act.date}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={classes.IdeasCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>Ehdotetut toimenpiteet</h3>
                <span style={{ fontSize: '0.65rem', background: '#f1f5f9', color: '#475569', padding: '0.15rem 0.4rem', borderRadius: '4px', fontWeight: 700 }}>STAATTINEN OHJEISTUS</span>
              </div>
              <div className={classes.IdeaItem}>
                <div className={classes.Title}>SEO Avainsanojen laajentaminen</div>
                <div className={classes.Text}>Luo uusia artikkeleita liittyen tähän toimialaan parantaaksesi orgaanista sijoitusta Google-haussa.</div>
              </div>
              <div className={classes.IdeaItem} style={{ borderLeftColor: '#10b981' }}>
                <div className={classes.Title}>B2B Outreach kampanja</div>
                <div className={classes.Text}>Käynnistä uusi onboarding outreach-ajo lisätäksesi kumppaneita alueille joissa match rate on matala.</div>
              </div>
              <div className={classes.IdeaItem} style={{ borderLeftColor: '#f59e0b' }}>
                <div className={classes.Title}>Tyytyväisyyskysely</div>
                <div className={classes.Text}>Lähetä automaattinen seurantakysely tarjouspyynnön jättäneille asiakkaille palautteen keräämiseksi.</div>
              </div>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.75rem', fontStyle: 'italic', textAlign: 'right' }}>
                Jatkovaihe: AI-pohjaiset dynaamiset toimenpidesuositukset
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Activity Creation Modal */}
      {showActivityModal && (
        <div className={classes.ModalOverlay}>
          <div className={classes.ModalContent}>
            <h3>Kirjaa uusi toimenpide</h3>
            <div className={classes.FormGroup}>
              <label>Toimenpiteen tyyppi</label>
              <select value={newActivityType} onChange={(e) => setNewActivityType(e.target.value)}>
                <option value="Sisältöpäivitys">📝 Sisältöpäivitys / SEO</option>
                <option value="B2B Kontaktointi">📞 B2B Kumppanitavoittelu</option>
                <option value="Some-kampanja">📣 Sosiaalisen median kampanja</option>
                <option value="Mainoskampanja">🚀 Maksettu mainonta</option>
              </select>
            </div>
            <div className={classes.FormGroup}>
              <label>Kuvaus tehdyistä toimista</label>
              <textarea placeholder="Kirjoita lyhyt kuvaus toimenpiteestä..." rows={3} value={newActivityDesc} onChange={(e) => setNewActivityDesc(e.target.value)} />
            </div>
            <div className={classes.ButtonGroup}>
              <button className={classes.Cancel} onClick={() => setShowActivityModal(false)}>Peruuta</button>
              <button className={classes.Submit} onClick={handleAddActivitySubmit}>Tallenna</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tab (The standard inputs and form fields) */}
      {activeTab === 'edit' && (
        <>
          {loading ? (
            <Spinner />
          ) : error && !service ? (
            <p style={{ color: '#ef4444', fontWeight: 500 }}>{error}</p>
          ) : (
            <>
              {createInput(EService.type)}
              {authUser?.isPrimary &&
                createInput(EService.operator, {
                  options: organizationOptions,
                  loading: organizationsLoading,
                })}
              {createInput(EService.name)}
              {createInput(EService.url)}
              {createInput(EService.partnerDomains)}

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', fontSize: '1.2rem', color: '#333' }}>Brändäys ja Laskeutumissivu</h3>
              <InputGroup>
                {createInput(EService.brandColor)}
                {createInput(EService.heroImage)}
              </InputGroup>
              {createInput(EService.logoBase64Text)}

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', fontSize: '1.2rem', color: '#333' }}>AI (Tekoäly) Asetukset</h3>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '0.5rem', color: '#555' }}>
                  AI tarjouspyyntö-ohjeistus (esim. mitä palvelu pitää sisällään)
                </label>
                <div style={{ padding: '1rem', backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: '4px', whiteSpace: 'pre-wrap', color: '#333', fontSize: '0.9rem' }}>
                  {promptFragmentText || "Ei erillistä ohjeistusta."}
                </div>
              </div>

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', fontSize: '1.2rem', color: '#333' }}>Integraatiot ja Analytiikka</h3>
              {createInput(EService.ga4PropertyId)}
              {createInput(EService.gscPropertyId)}

              <h3 style={{ marginTop: '2rem', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', fontSize: '1.2rem', color: '#333' }}>Sijainnit ja Asetukset</h3>
              <InputGroup>
                {createInput(EService.locations, {
                  options: locationOptions,
                  loading: locationsLoading,
                })}
                {locationOptions && (
                  <Button onClick={addAllLocationsHandler}>
                    {t(ETranslation.COMMON_ADD_ALL)}
                  </Button>
                )}
              </InputGroup>

              {createInput(EService.archived)}
              {Routes.isNotNew(id) && createInput(EService.serviceToken)}
            </>
          )}

          <InputGroup style={{ marginTop: '2.5rem' }}>
            <Button disabled={loading} onClick={submitHandler}>
              {t(ETranslation.COMMON_SAVE)}
            </Button>
            <Button onClick={() => history.goBack()}>
              {t(ETranslation.COMMON_RETURN)}
            </Button>
            {Routes.isNotNew(id) && authUser?.isPrimary && (
              <Button disabled={loading} onClick={setConfirmDeleteDialog}>
                {t(ETranslation.COMMON_REMOVE)}
              </Button>
            )}
          </InputGroup>
        </>
      )}

      {/* AI Chat Copilot Tab */}
      {activeTab === 'chat' && Routes.isNotNew(id) && (
        <div className={classes.ChatContainer}>
          <div className={classes.ChatMessages}>
            {chatMessages.map((msg, index) => (
              <div key={index} className={`${classes.ChatMessage} ${msg.role === 'user' ? classes.User : classes.Assistant}`}>
                <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
              </div>
            ))}
            {chatSending && (
              <div className={classes.ChatMessage + " " + classes.Assistant}>
                <Spinner />
                <span style={{ fontSize: '0.85rem', color: '#64748b', marginLeft: '0.5rem' }}>AI-Avustaja miettii vastausta...</span>
              </div>
            )}
          </div>

          {/* Quick Preset Action Chips for Operators */}
          <div style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem 1rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
            <button onClick={() => handleSendChatMessage("Generoi 5 parasta SEO-avainsanaa tälle toimialalle ja ehdota niille blogiartikkelien otsikoita.")} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600, color: '#4f46e5' }}>
              🔑 Generoi SEO-avainsanoja
            </button>
            <button onClick={() => handleSendChatMessage("Suunnittele lyhyt B2B-kontaktointikampanja uusia palveluntarjoajia varten.")} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600, color: '#10b981' }}>
              📞 Suunnittele B2B-kampanja
            </button>
            <button onClick={() => handleSendChatMessage("Miten voimme parantaa tämän palvelukategorian konversioprosenttia laskeutumissivulla?")} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600, color: '#f59e0b' }}>
              📈 Paranna konversiota
            </button>
          </div>

          <div className={classes.ChatInputArea}>
            <input type="text" placeholder="Kirjoita viesti tekoälyavustajalle..." value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSendChatMessage()} disabled={chatSending} />
            <button onClick={() => handleSendChatMessage()} disabled={chatSending || !chatInput.trim()}>
              Lähetä
            </button>
          </div>
        </div>
      )}
    </Container>
  );
};

export default Service;
